import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Badge,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { supplierInvoicesApi } from '../../services/api';

const UploadInvoiceModal = ({ 
  open, 
  onClose, 
  onSuccess 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parsedInvoices, setParsedInvoices] = useState([]); // Array di fatture parsate
  const [parseErrors, setParseErrors] = useState([]); // Errori di parsing per file
  const [importResults, setImportResults] = useState(null); // Risultati importazione
  const [error, setError] = useState(null);
  const [parseProgress, setParseProgress] = useState(0);
  const [importProgress, setImportProgress] = useState(0);
  const [expandedInvoice, setExpandedInvoice] = useState(null);

  const resetState = useCallback(() => {
    setSelectedFiles([]);
    setParsedInvoices([]);
    setParseErrors([]);
    setImportResults(null);
    setError(null);
    setParsing(false);
    setImporting(false);
    setParseProgress(0);
    setImportProgress(0);
    setExpandedInvoice(null);
  }, []);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Filtra solo file XML
    const xmlFiles = files.filter(file => {
      const ext = file.name.toLowerCase().split('.').pop();
      return ext === 'xml';
    });

    if (xmlFiles.length === 0) {
      setError('Seleziona almeno un file XML valido (fattura elettronica FatturaPA)');
      return;
    }

    if (xmlFiles.length < files.length) {
      setError(`${files.length - xmlFiles.length} file ignorati (non XML). Caricati ${xmlFiles.length} file XML.`);
    } else {
      setError(null);
    }

    setSelectedFiles(prev => [...prev, ...xmlFiles]);
    setParsedInvoices([]);
    setParseErrors([]);
    setImportResults(null);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setParsedInvoices([]);
    setParseErrors([]);
  };

  const handleParse = async () => {
    if (selectedFiles.length === 0) return;

    setParsing(true);
    setError(null);
    setParsedInvoices([]);
    setParseErrors([]);
    setParseProgress(0);

    const parsed = [];
    const errors = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      setParseProgress(Math.round(((i + 1) / selectedFiles.length) * 100));

      try {
        const xmlContent = await file.text();
        const result = await supplierInvoicesApi.parse(xmlContent, file.name);
        
        if (result.success) {
          parsed.push({
            ...result.data,
            _fileName: file.name,
            // Non selezionare di default le fatture già importate
            _selected: !result.data.alreadyImported
          });
        } else {
          errors.push({
            fileName: file.name,
            error: result.error || 'Errore durante il parsing'
          });
        }
      } catch (err) {
        console.error(`Errore parsing ${file.name}:`, err);
        errors.push({
          fileName: file.name,
          error: err.response?.data?.error || 'Errore durante il parsing del file'
        });
      }
    }

    setParsedInvoices(parsed);
    setParseErrors(errors);
    setParsing(false);
  };

  const toggleInvoiceSelection = (index) => {
    setParsedInvoices(prev => prev.map((inv, i) => 
      i === index ? { ...inv, _selected: !inv._selected } : inv
    ));
  };

  const handleImport = async () => {
    const toImport = parsedInvoices.filter(inv => inv._selected);
    if (toImport.length === 0) {
      setError('Seleziona almeno una fattura da importare');
      return;
    }

    setImporting(true);
    setError(null);
    setImportProgress(0);

    const results = {
      success: [],
      errors: []
    };

    for (let i = 0; i < toImport.length; i++) {
      const invoice = toImport[i];
      setImportProgress(Math.round(((i + 1) / toImport.length) * 100));

      try {
        const result = await supplierInvoicesApi.import(invoice);
        
        if (result.success) {
          results.success.push({
            fileName: invoice._fileName,
            invoiceNumber: invoice.numero_documento,
            data: result.data
          });
        } else {
          results.errors.push({
            fileName: invoice._fileName,
            invoiceNumber: invoice.numero_documento,
            error: result.error || 'Errore durante l\'importazione'
          });
        }
      } catch (err) {
        console.error(`Errore importazione ${invoice._fileName}:`, err);
        results.errors.push({
          fileName: invoice._fileName,
          invoiceNumber: invoice.numero_documento,
          error: err.response?.data?.error || 'Errore durante l\'importazione'
        });
      }
    }

    setImportResults(results);
    setImporting(false);

    // Se tutte importate con successo, chiudi dopo un po'
    if (results.errors.length === 0 && results.success.length > 0) {
      setTimeout(() => {
        onSuccess(results.success.map(r => r.data));
        handleClose();
      }, 1500);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/D';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  const selectedCount = parsedInvoices.filter(inv => inv._selected).length;
  const totalAmount = parsedInvoices
    .filter(inv => inv._selected)
    .reduce((sum, inv) => sum + (inv.totale_documento || 0), 0);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudUploadIcon color="primary" />
          Importa Fatture XML
          {selectedFiles.length > 0 && (
            <Chip 
              label={`${selectedFiles.length} file`} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Risultati importazione */}
        {importResults && (
          <Box sx={{ mb: 2 }}>
            {importResults.success.length > 0 && (
              <Alert severity="success" sx={{ mb: 1 }}>
                <Typography fontWeight="bold">
                  {importResults.success.length} fattur{importResults.success.length === 1 ? 'a importata' : 'e importate'} con successo!
                </Typography>
                <List dense>
                  {importResults.success.map((r, idx) => (
                    <ListItem key={idx} sx={{ py: 0 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, mr: 1, color: 'success.main' }} />
                      <Typography variant="body2">
                        {r.invoiceNumber} - {r.fileName}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}
            {importResults.errors.length > 0 && (
              <Alert severity="error">
                <Typography fontWeight="bold">
                  {importResults.errors.length} fattur{importResults.errors.length === 1 ? 'a non importata' : 'e non importate'}
                </Typography>
                <List dense>
                  {importResults.errors.map((r, idx) => (
                    <ListItem key={idx} sx={{ py: 0, flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ErrorIcon sx={{ fontSize: 16, mr: 1, color: 'error.main' }} />
                        <Typography variant="body2">
                          {r.invoiceNumber} - {r.fileName}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="error" sx={{ pl: 3 }}>
                        {r.error}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}
          </Box>
        )}

        {/* Area upload - solo se non ci sono risultati importazione */}
        {!importResults && (
          <>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                mb: 2,
                textAlign: 'center',
                bgcolor: selectedFiles.length > 0 ? 'success.50' : 'grey.50',
                borderStyle: 'dashed',
                borderWidth: 2,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'primary.50' }
              }}
              component="label"
            >
              <input
                type="file"
                accept=".xml"
                multiple
                hidden
                onChange={handleFileSelect}
              />
              
              <CloudUploadIcon sx={{ fontSize: 48, color: selectedFiles.length > 0 ? 'success.main' : 'grey.400', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                {selectedFiles.length > 0 
                  ? `${selectedFiles.length} file selezionati - Clicca per aggiungerne altri`
                  : 'Trascina i file XML qui'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                oppure clicca per selezionare i file
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Formato supportato: FatturaPA (XML) - Selezione multipla abilitata
              </Typography>
            </Paper>

            {/* Lista file selezionati */}
            {selectedFiles.length > 0 && parsedInvoices.length === 0 && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  File selezionati ({selectedFiles.length})
                </Typography>
                <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {selectedFiles.map((file, idx) => (
                    <ListItem 
                      key={idx}
                      secondaryAction={
                        <IconButton edge="end" size="small" onClick={() => removeFile(idx)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <ListItemText 
                        primary={file.name}
                        secondary={`${(file.size / 1024).toFixed(1)} KB`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            {/* Progress parsing */}
            {parsing && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Analisi in corso... {parseProgress}%
                </Typography>
                <LinearProgress variant="determinate" value={parseProgress} />
              </Box>
            )}

            {/* Pulsante Parse */}
            {selectedFiles.length > 0 && parsedInvoices.length === 0 && !parsing && (
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleParse}
                  disabled={parsing}
                  startIcon={parsing ? <CircularProgress size={20} /> : null}
                >
                  Analizza {selectedFiles.length} Fattur{selectedFiles.length === 1 ? 'a' : 'e'}
                </Button>
              </Box>
            )}

            {/* Errori di parsing */}
            {parseErrors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography fontWeight="bold">
                  {parseErrors.length} file non analizzati correttamente:
                </Typography>
                <List dense>
                  {parseErrors.map((err, idx) => (
                    <ListItem key={idx} sx={{ py: 0 }}>
                      <ErrorIcon sx={{ fontSize: 16, mr: 1, color: 'warning.main' }} />
                      <Typography variant="body2">
                        {err.fileName}: {err.error}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}

            {/* Fatture parsate */}
            {parsedInvoices.length > 0 && (
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography fontWeight="bold">
                    {parsedInvoices.length} fattur{parsedInvoices.length === 1 ? 'a analizzata' : 'e analizzate'} correttamente!
                  </Typography>
                  <Typography variant="body2">
                    Selezionate: {selectedCount} | Totale: {formatCurrency(totalAmount)}
                  </Typography>
                </Alert>

                {/* Lista fatture con accordion */}
                {parsedInvoices.map((invoice, idx) => (
                  <Accordion 
                    key={idx}
                    expanded={expandedInvoice === idx}
                    onChange={() => setExpandedInvoice(expandedInvoice === idx ? null : idx)}
                    sx={{ 
                      mb: 1,
                      opacity: invoice._selected ? 1 : 0.6,
                      border: invoice._selected ? '2px solid' : '1px solid',
                      borderColor: invoice._selected ? 'success.main' : 'grey.300'
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleInvoiceSelection(idx);
                          }}
                          color={invoice._selected ? 'success' : 'default'}
                        >
                          {invoice._selected ? <CheckCircleIcon /> : <ErrorIcon color="disabled" />}
                        </IconButton>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography fontWeight="bold">
                            {invoice.numero_documento} - {formatDate(invoice.data_documento)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {invoice.cedente?.denominazione} | {invoice._fileName}
                          </Typography>
                        </Box>
                        {invoice.alreadyImported && (
                          <Chip 
                            icon={<HistoryIcon />}
                            label="Già importata"
                            color="error"
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {invoice.plantMatchSummary?.hasUnmatchedPlants && (
                          <Chip 
                            icon={<WarningIcon />}
                            label={`${invoice.plantMatchSummary.unmatched} imp. non trovati`}
                            color="warning"
                            size="small"
                          />
                        )}
                        <Chip 
                          label={formatCurrency(invoice.totale_documento)}
                          color="primary"
                          size="small"
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        {/* Avviso fattura già importata */}
                        {invoice.alreadyImported && (
                          <Grid item xs={12}>
                            <Alert severity="error" sx={{ mb: 1 }}>
                              <Typography variant="subtitle2">
                                ⛔ Questa fattura è già stata importata in data {new Date(invoice.existingInvoiceDate).toLocaleDateString('it-IT')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Se la importi nuovamente verrà creato un duplicato.
                              </Typography>
                            </Alert>
                          </Grid>
                        )}
                        
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" color="primary">Fornitore</Typography>
                          <Typography>{invoice.cedente?.denominazione}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            P.IVA: {invoice.cedente?.partita_iva}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" color="primary">Totali</Typography>
                          <Typography variant="body2">
                            Imponibile: {formatCurrency(invoice.totale_imponibile)}
                          </Typography>
                          <Typography variant="body2">
                            IVA: {formatCurrency(invoice.totale_iva)}
                          </Typography>
                          <Typography fontWeight="bold">
                            Totale: {formatCurrency(invoice.totale_documento)}
                          </Typography>
                        </Grid>
                        
                        {/* Avviso impianti non trovati */}
                        {invoice.plantMatchSummary?.hasUnmatchedPlants && (
                          <Grid item xs={12}>
                            <Alert severity="warning" sx={{ mb: 1 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                ⚠️ {invoice.plantMatchSummary.unmatched} di {invoice.plantMatchSummary.total} righe con codice impianto non trovato:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                {(invoice.righe || []).filter(r => !r.plant_matched && r.codice_impianto).map((riga, cidx) => (
                                  <Chip 
                                    key={cidx}
                                    label={riga.codice_impianto}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Questi DDT verranno importati ma non saranno collegati ad alcun impianto. 
                                Verifica i codici consegna negli impianti.
                              </Typography>
                            </Alert>
                          </Grid>
                        )}
                        
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="primary">
                            Righe ({invoice.righe?.length || 0})
                          </Typography>
                          <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                            {(invoice.righe || []).map((riga, ridx) => (
                              <ListItem 
                                key={ridx} 
                                sx={{ 
                                  py: 0.5,
                                  bgcolor: (riga.codice_impianto && !riga.plant_matched) ? 'warning.light' : 'transparent',
                                  borderRadius: 1,
                                  mb: 0.5
                                }}
                              >
                                <ListItemText
                                  primaryTypographyProps={{ component: 'div' }}
                                  secondaryTypographyProps={{ component: 'div' }}
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {riga.codice_impianto && !riga.plant_matched && (
                                        <WarningIcon color="warning" fontSize="small" />
                                      )}
                                      {riga.plant_matched && (
                                        <CheckCircleIcon color="success" fontSize="small" />
                                      )}
                                      <Typography variant="body2">
                                        {riga.descrizione || `Riga ${riga.numero_linea}`}
                                      </Typography>
                                    </Box>
                                  }
                                  secondary={
                                    <>
                                      <Typography variant="caption" component="span">
                                        Qtà: {riga.quantita?.toLocaleString('it-IT')} {riga.unita_misura || ''} - {formatCurrency(riga.prezzo_totale)}
                                      </Typography>
                                      {riga.codice_impianto && (
                                        <>
                                          <br />
                                          <Typography variant="caption" component="span">
                                            Cod. Consegna: <strong>{riga.codice_impianto}</strong>
                                          </Typography>
                                          {riga.plant_matched ? (
                                            <Chip 
                                              label={`${riga.plant_name} (${riga.plant_city})`} 
                                              size="small" 
                                              color="success" 
                                              sx={{ ml: 1, height: 18, fontSize: '0.7rem' }} 
                                            />
                                          ) : (
                                            <Chip 
                                              label="Non trovato" 
                                              size="small" 
                                              color="warning" 
                                              sx={{ ml: 1, height: 18, fontSize: '0.7rem' }} 
                                            />
                                          )}
                                        </>
                                      )}
                                    </>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}

                {/* Progress importazione */}
                {importing && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Importazione in corso... {importProgress}%
                    </Typography>
                    <LinearProgress variant="determinate" value={importProgress} />
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={parsing || importing}>
          {importResults ? 'Chiudi' : 'Annulla'}
        </Button>
        {parsedInvoices.length > 0 && !importResults && (
          <Button 
            onClick={handleImport} 
            variant="contained" 
            color="success"
            disabled={importing || selectedCount === 0}
            startIcon={importing ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {importing 
              ? 'Importazione...' 
              : `Importa ${selectedCount} Fattur${selectedCount === 1 ? 'a' : 'e'}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UploadInvoiceModal;
